import { useEffect, useState } from "react";
import { Text } from "../text/Text";

import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { useMediaQuery, useTheme } from "@mui/material";

type HorizontalScrollListProps<T> = {
  renderItem: (item: T, index: number) => React.ReactNode;
  keyExtractor: (item: T, index: number) => string;
  items: T[];
  columnGap?: React.CSSProperties["columnGap"];
  hideScrollBar?: boolean;
} & (
  | {
      fullScreenItems?: true;
      /** paginate means that the list will be paginated and the items will be displayed in a grid */
      paginate?: true;
      navigationSettings?:
        | {
            show: true;
            panel?:
              | {
                  position: "inside";
                  visibility?: "always" | "onTap" | "visible" | "none";
                }
              | {
                  position: "outside";
                  visibility?: "always" | "none";
                }
              | undefined;
          }
        | {
            show: false;
          };
    }
  | {
      fullScreenItems?: false;
      paginate?: false;
      navigationSettings?: undefined;
    }
);

const HorizontalScrollList = <T,>({
  renderItem,
  keyExtractor,
  items = [],
  paginate = false,
  columnGap,
  navigationSettings,
  fullScreenItems = false,
  hideScrollBar = false,
}: HorizontalScrollListProps<T>) => {
  const [_prefix, _] = useState(() =>
    Math.random().toString(36).substring(2, 15)
  );
  const id = _prefix + "hscroll-list";

  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [showArrows, setShowArrows] = useState(false);

  const nextItem = async () => {
    // scroll to the next item
    const element = document.getElementById(id);
    if (element) {
      const scrollLeft = element.scrollLeft;
      const clientWidth = element.clientWidth;
      const scrollWidth = element.scrollWidth;
      if (scrollLeft + clientWidth < scrollWidth) {
        element.scrollTo({
          left: scrollLeft + clientWidth,
          behavior: "smooth",
        });
      }
    }
  };

  const prevItem = async () => {
    // scroll to the previous item
    const element = document.getElementById(id);
    if (element) {
      const scrollLeft = element.scrollLeft;
      const clientWidth = element.clientWidth;
      if (scrollLeft > 0) {
        element.scrollTo({
          left: scrollLeft - clientWidth,
          behavior: "smooth",
        });
      }
    }
  };

  const activatesOnTap =
    navigationSettings?.show &&
    navigationSettings.panel?.visibility === "onTap";
  const activatesOnVisible =
    navigationSettings?.show &&
    navigationSettings.panel?.visibility === "visible";
  const activatesAlways =
    navigationSettings?.show &&
    navigationSettings.panel?.visibility === "always";

  useEffect(() => {
    if (fullScreenItems && (activatesAlways || activatesOnVisible)) {
      setShowArrows(true);
    }
  }, []);

  const handleShowArrows = (e: React.MouseEvent) => {
    // make sure we ignore the click on the arrows
    const target = e.target as HTMLElement;
    if (target.id !== `${id}-prev` && target.id !== `${id}-next`) {
      setShowArrows(!showArrows);
    }
  };

  return (
    <div
      style={{
        position: "relative",
        maxWidth: isMobile ? "100vw" : "100%",
      }}
    >
      <div
        onClick={(e) => {
          if (
            navigationSettings?.show &&
            (activatesOnTap || activatesOnVisible) &&
            !activatesAlways
          ) {
            handleShowArrows(e);
          }
        }}
        id={id}
        className="scroll-x"
        style={{
          paddingBottom: "40px",
          overflowX: "auto",
          scrollSnapType: paginate ? "x mandatory" : "none",
          WebkitOverflowScrolling: "touch",
          overflowY: "hidden",
          scrollbarWidth: hideScrollBar ? "none" : "auto",
        }}
      >
        {items.length > 1 &&
          navigationSettings?.show &&
          navigationSettings.panel?.position === "inside" && (
            <div
              style={{
                visibility: showArrows ? "visible" : "hidden",
                position: "sticky",
                top: "50%",
                left: "0",
                zIndex: 1000,
                display: "flex",
                justifyContent: "space-between",
                paddingLeft: "10px",
                paddingRight: "10px",
              }}
            >
              <ArrowBack
                id={`${id}-prev`}
                onClick={prevItem}
                style={{
                  cursor: "pointer",
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "50%",
                  padding: "10px",
                }}
              />
              <ArrowForward
                id={`${id}-next`}
                onClick={nextItem}
                style={{
                  cursor: "pointer",
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: "50%",
                  padding: "10px",
                }}
              />
            </div>
          )}
        <div
          style={{
            display: "flex",
            columnGap: columnGap,
          }}
        >
          {items.map((item, index) => (
            <div
              key={keyExtractor(item, index)}
              style={{
                flex: fullScreenItems ? "0 0 100%" : "0 0 auto",
                scrollSnapAlign: paginate ? "start" : "none",
                boxSizing: "border-box",
              }}
            >
              {renderItem(item, index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HorizontalScrollList;
